import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import defaultImg from 'src/assets/images/no-user-picture.svg';
import { Rating } from '@material-ui/lab';
import { BaseTypography } from 'src/components/common';
import { BaseChipsList } from 'src/components/common/BaseChip/BaseChipsList';
import { useDeviceSizeType } from 'src/utils/hooks/useDeviceType';
import { MediaSection } from './mediaViewer/MediaSection';
import { IGuestInfo } from '../../../api/base/Guest/types';
import { getReadableDate } from '../../../utils/helpers';
import { Grid, Link } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  rootInfo: {
    display: 'grid',
    gridTemplateColumns: '80px 345px 300px',
    gridGap: '46px',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '80px 200px 1fr',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      gridGap: '15px',
    },
  },
  info__avatarSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  info__avatar: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    objectFit: 'cover',
    [theme.breakpoints.down('sm')]: {
      width: '50px',
      height: '50px',
    },
  },
  info__rating: {
    marginTop: '8px',
  },
  info__fields: {
    display: 'grid',
    gridGap: '14px',
    wordBreak: 'break-all',
  },
  info__fieldLabel: {
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '140%',
    letterSpacing: '0.03em',
    color: '#949C9E',
  },

  info__companySection: {
    display: 'grid',
    gridAutoRows: 'max-content',
    gridGap: '8px',
    alignItems: 'baseline',
  },
  media: {
    margin: '20px 0',
  },
  link: {
    color: theme.palette.primary.contrastText,
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '18px',
    textDecoration: 'none',
  },
}));

export type PContactOverview = {
  guest: IGuestInfo;
};

export const ContactOverview: React.FC<PContactOverview> = (props) => {
  const { guest } = props;
  const classes = useStyles();

  return (
    <Grid container>
      <Grid item md="auto" xs={12}>
        <div style={{ marginRight: 22, display: 'block' }}>
          <img
            src={guest?.avatar || defaultImg}
            alt="User Avatar"
            className={classes.info__avatar}
          />
          <br />
          <Rating
            className={classes.info__rating}
            size="small"
            value={guest?.rating || 0}
            precision={0.5}
            readOnly
          />
          <div style={{ marginTop: 20 }}>
            <Link
              href={guest?.linkedin}
              target="_blank"
              rel="noreferrer"
              className={classes.link}>
              LinkedIn
            </Link>
          </div>
        </div>
      </Grid>
      <Grid item md="auto" xs={12}>
        <div style={{ marginRight: 33 }}>
          <BaseTypography variant="h4">
            {guest?.first_name} {guest?.last_name}
          </BaseTypography>
          <BaseTypography style={{ padding: '10px 0' }} variant="tag">
            Areas of Expertise
          </BaseTypography>
          <BaseChipsList
            list={guest?.expertise.map((item) => item.name) || []}
          />
          <BaseTypography style={{ padding: '10px 0' }} variant="tag">
            Available for appearances in
          </BaseTypography>
          <BaseChipsList
            list={guest?.available.map((item) => item.name) || []}
          />
          {guest?.last_date_trained && (
            <>
              <br />
              <BaseTypography className={classes.info__fieldLabel}>
                Last date trained
              </BaseTypography>
              <BaseTypography variant="textMedium">
                {getReadableDate(guest?.last_date_trained)}
              </BaseTypography>
            </>
          )}
        </div>
      </Grid>
      <Grid item md={4} xs={12}>
        <div className={classes.info__fields}>
          <div>
            <BaseTypography className={classes.info__fieldLabel}>
              Company name
            </BaseTypography>
            <BaseTypography variant="textMedium">
              {guest?.company_name}
            </BaseTypography>
          </div>
          <div>
            <BaseTypography className={classes.info__fieldLabel}>
              Title
            </BaseTypography>
            <BaseTypography variant="textMedium">{guest?.title}</BaseTypography>
          </div>
          <div>
            <BaseTypography className={classes.info__fieldLabel}>
              Country
            </BaseTypography>
            <BaseTypography variant="textMedium">
              {guest?.country &&
                guest?.country?.map((item) => item.name).join(',')}
            </BaseTypography>
          </div>
        </div>
      </Grid>
      <Grid item md={12}>
        <MediaSection
          className={classes.media}
          videos={guest?.videos || []}
          images={guest?.images || []}
        />
      </Grid>
    </Grid>
  );
};
